import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, SkipBack, SkipForward, Shuffle, Volume2, VolumeX, ChevronDown, Plus } from "lucide-react";
import { SubmitTrackDialog } from "./SubmitTrackDialog";

const API = process.env.REACT_APP_BACKEND_URL;

const DISC_SRC = "/assets/disc-solana.png";
const DISC_WEBP = "/assets/disc-solana.webp";
const DISC_AVIF = "/assets/disc-solana.avif";

export const MusicPlayer = () => {
  const audioRef = useRef(null);
  const audioCtxRef = useRef(null);
  const gainRef = useRef(null);
  const audioWiredRef = useRef(false);
  const [tracks, setTracks] = useState([]);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [muted, setMuted] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  // Disc rotation is driven entirely by CSS (compositor thread) — no React
  // state per frame, no rAF loop competing with scroll on mobile.
  // The `playing` state just toggles the CSS `animation-play-state`.

  // Load tracks (public endpoint) — never autoplay.
  // The starting track is randomized per page load so every visitor
  // hears a different song first. After the initial pick, prev/next
  // and shuffle behave normally.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/tracks`);
        if (!alive) return;
        const list = data.tracks || [];
        setTracks(list);
        if (list.length > 0) {
          setIndex(Math.floor(Math.random() * list.length));
        }
      } catch {
        /* ignore */
      }
    })();
    return () => { alive = false; };
  }, []);

  // Wire the <audio> through a Web Audio API GainNode so volume works on iOS
  // Safari (which ignores audio.volume). MUST run inside a user gesture, so we
  // build it lazily inside togglePlay(). Falls back to audio.volume otherwise.
  const ensureAudioGraph = useCallback(() => {
    if (audioWiredRef.current) return;
    const a = audioRef.current;
    if (!a) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    try {
      const ctx = new AC();
      const src = ctx.createMediaElementSource(a);
      const gain = ctx.createGain();
      gain.gain.value = muted ? 0 : volume;
      src.connect(gain).connect(ctx.destination);
      audioCtxRef.current = ctx;
      gainRef.current = gain;
      audioWiredRef.current = true;
    } catch {
      /* createMediaElementSource fails on double-wire; ignore */
      audioWiredRef.current = true;
    }
  }, [muted, volume]);

  // Volume + mute — write to GainNode (iOS-friendly) AND element (fallback).
  useEffect(() => {
    const v = muted ? 0 : volume;
    if (gainRef.current) {
      try { gainRef.current.gain.value = v; } catch { /* ignore */ }
    }
    if (audioRef.current) {
      audioRef.current.volume = v;
      audioRef.current.muted = muted;
    }
  }, [volume, muted]);

  // Continuous rotation loop — replaced with pure CSS (see .disc-spin).

  const currentTrack = tracks[index] || null;

  const play = useCallback(async () => {
    const a = audioRef.current;
    if (!a || !currentTrack) return;
    ensureAudioGraph();
    // Resume AudioContext on user gesture (iOS/Chrome autoplay policy).
    if (audioCtxRef.current && audioCtxRef.current.state !== "running") {
      try { await audioCtxRef.current.resume(); } catch { /* ignore */ }
    }
    try {
      await a.play();
      setPlaying(true);
    } catch {
      setPlaying(false);
    }
  }, [currentTrack, ensureAudioGraph]);

  const pause = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    a.pause();
    setPlaying(false);
  }, []);

  const togglePlay = useCallback(() => {
    if (playing) pause();
    else play();
  }, [playing, play, pause]);

  // ─── Proper deck-shuffle ──────────────────────────────────────────────
  // Instead of picking a random index on each next(), we build a shuffled
  // permutation of ALL track indices ("the deck") and walk through it in
  // order. When we reach the end, we reshuffle for the next round. This
  // guarantees every song plays once before any repeats.
  const deckRef = useRef([]);   // Fisher-Yates permutation of [0..tracks-1]
  const deckPosRef = useRef(0); // current position within the deck

  const buildDeck = useCallback((firstIdx = null) => {
    const n = tracks.length;
    if (n === 0) return [];
    const idxs = Array.from({ length: n }, (_, i) => i);
    // Fisher-Yates
    for (let i = n - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [idxs[i], idxs[j]] = [idxs[j], idxs[i]];
    }
    // If there's a track we want at position 0 (e.g. the one currently
    // playing when shuffle was just turned on), move it into place.
    if (firstIdx != null && n > 1) {
      const p = idxs.indexOf(firstIdx);
      if (p > 0) [idxs[0], idxs[p]] = [idxs[p], idxs[0]];
    }
    return idxs;
  }, [tracks.length]);

  // Build the deck when shuffle turns on OR the track list itself arrives/changes.
  // We intentionally do NOT depend on `index` — that would rebuild the deck every
  // time the current track changes, defeating the "no repeats until all played" rule.
  useEffect(() => {
    if (!shuffle || !tracks.length) {
      deckRef.current = [];
      deckPosRef.current = 0;
      return;
    }
    deckRef.current = buildDeck(index);
    deckPosRef.current = 0;
  }, [shuffle, tracks.length, buildDeck]);

  const pickNext = useCallback((dir) => {
    if (!tracks.length) return 0;
    if (shuffle && tracks.length > 1 && deckRef.current.length === tracks.length) {
      let pos = deckPosRef.current + dir;
      // prev at the start of the deck: stay put (there's nothing "before" it
      // in the current shuffled round).
      if (pos < 0) pos = 0;
      // Ran off the end of the deck → reshuffle for the next round, but
      // guard against the just-played track landing at position 0 (which
      // would play the same song twice in a row at the boundary).
      if (pos >= deckRef.current.length) {
        const justPlayed = deckRef.current[deckRef.current.length - 1];
        let newDeck = buildDeck(null);
        if (newDeck.length > 1 && newDeck[0] === justPlayed) {
          [newDeck[0], newDeck[1]] = [newDeck[1], newDeck[0]];
        }
        deckRef.current = newDeck;
        pos = 0;
      }
      deckPosRef.current = pos;
      return deckRef.current[pos];
    }
    return (index + dir + tracks.length) % tracks.length;
  }, [tracks, index, shuffle, buildDeck]);

  const next = useCallback(() => setIndex(pickNext(1)), [pickNext]);
  const prev = useCallback(() => setIndex(pickNext(-1)), [pickNext]);

  // Auto-advance on ended — reuses the same deck-shuffle path.
  const onEnded = useCallback(() => {
    setIndex(pickNext(1));
  }, [pickNext]);

  // When index changes while playing, autoplay the new track.
  const wasPlayingRef = useRef(false);
  useEffect(() => {
    wasPlayingRef.current = playing;
  }, [playing]);
  useEffect(() => {
    const a = audioRef.current;
    if (!a || !currentTrack) return;
    a.load();
    if (wasPlayingRef.current) {
      a.play().catch(() => {});
    }
  }, [index, currentTrack?.url]);

  if (!tracks.length) return null;

  return (
    <>
      <audio
        ref={audioRef}
        src={currentTrack?.url}
        onEnded={onEnded}
        preload="none"
        data-testid="music-audio-element"
      />

      {/* Floating disc + expandable panel */}
      <div
        className="fixed z-40 select-none"
        style={{ right: "clamp(12px, 3vw, 24px)", bottom: "clamp(12px, 3vw, 24px)" }}
        data-testid="music-player-widget"
      >
        <AnimatePresence>
          {expanded && (
            <motion.div
              key="panel"
              initial={{ opacity: 0, y: 24, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.92 }}
              transition={{ type: "spring", stiffness: 260, damping: 24 }}
              data-testid="music-player-panel"
              className="absolute bottom-[calc(100%+12px)] right-0 w-[min(92vw,340px)] rounded-2xl border border-[#9945FF]/40 bg-[#0a0714]/95 backdrop-blur-xl shadow-[0_10px_50px_rgba(153,69,255,0.35)] p-4"
            >
              <div className="flex items-start gap-3">
                <div className="relative shrink-0">
                  <picture>
                    <source srcSet={DISC_AVIF} type="image/avif" />
                    <source srcSet={DISC_WEBP} type="image/webp" />
                    <img
                      src={DISC_SRC}
                      alt="disc"
                      loading="lazy"
                      decoding="async"
                      className="disc-spin w-14 h-14 rounded-full border border-[#9945FF]/60"
                      data-spinning={playing ? "1" : "0"}
                    />
                  </picture>
                  <span className="absolute inset-0 rounded-full pointer-events-none" style={{ boxShadow: "inset 0 0 12px rgba(0,0,0,0.65)" }} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-bebas tracking-[0.28em] text-[#14F195] text-xs">NOW PLAYING</p>
                  <p
                    data-testid="music-current-title"
                    className="font-marker text-white text-base truncate mt-0.5"
                    title={currentTrack?.title}
                  >
                    {currentTrack?.title || "—"}
                  </p>
                  <p className="text-zinc-400 text-xs truncate">{currentTrack?.artist || ""}</p>
                </div>
                <button
                  type="button"
                  aria-label="Collapse player"
                  data-testid="music-collapse-button"
                  onClick={() => setExpanded(false)}
                  className="text-zinc-400 hover:text-[#14F195] p-1 -mt-1 -mr-1 transition-colors"
                >
                  <ChevronDown size={20} />
                </button>
              </div>

              <div className="mt-4 flex items-center justify-center gap-2">
                <button
                  type="button"
                  aria-label="Previous track"
                  data-testid="music-prev-button"
                  onClick={prev}
                  className="text-zinc-300 hover:text-white hover:bg-[#9945FF]/20 rounded-full p-2 transition-colors"
                >
                  <SkipBack size={20} />
                </button>
                <button
                  type="button"
                  aria-label={playing ? "Pause" : "Play"}
                  data-testid="music-play-toggle-button"
                  onClick={togglePlay}
                  className="rounded-full w-12 h-12 flex items-center justify-center btn-buy"
                  style={{ boxShadow: "0 0 20px rgba(20,241,149,0.55)" }}
                >
                  {playing ? <Pause size={22} className="text-[#050409] fill-[#050409]" /> : <Play size={22} className="text-[#050409] fill-[#050409] ml-0.5" />}
                </button>
                <button
                  type="button"
                  aria-label="Next track"
                  data-testid="music-next-button"
                  onClick={next}
                  className="text-zinc-300 hover:text-white hover:bg-[#9945FF]/20 rounded-full p-2 transition-colors"
                >
                  <SkipForward size={20} />
                </button>
              </div>

              <div className="mt-4 flex items-center gap-2">
                <button
                  type="button"
                  aria-pressed={shuffle}
                  aria-label={shuffle ? "Shuffle on" : "Shuffle off"}
                  data-testid="music-shuffle-button"
                  onClick={() => setShuffle((s) => !s)}
                  className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bebas tracking-[0.2em] border transition-colors shrink-0 ${
                    shuffle
                      ? "border-[#14F195] text-[#050409] bg-gradient-to-r from-[#14F195] to-[#9945FF]"
                      : "border-[#9945FF]/40 text-zinc-300 hover:border-[#9945FF] hover:text-white"
                  }`}
                >
                  <Shuffle size={13} /> SHUFFLE {shuffle ? "ON" : "OFF"}
                </button>

                <button
                  type="button"
                  aria-label={muted ? "Unmute" : "Mute"}
                  data-testid="music-mute-button"
                  onClick={() => setMuted((m) => !m)}
                  className="text-zinc-300 hover:text-[#14F195] p-1 transition-colors shrink-0"
                >
                  {muted || volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </button>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={muted ? 0 : volume}
                  data-testid="music-volume-slider"
                  aria-label="Volume"
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setVolume(v);
                    if (v > 0 && muted) setMuted(false);
                  }}
                  onInput={(e) => {
                    const v = Number(e.target.value);
                    setVolume(v);
                    if (v > 0 && muted) setMuted(false);
                  }}
                  className="flex-1 accent-[#14F195] h-2 cursor-pointer touch-manipulation"
                />
                <span
                  data-testid="music-volume-readout"
                  className="text-[10px] font-bebas tracking-[0.15em] text-zinc-400 w-8 text-right shrink-0"
                >
                  {Math.round((muted ? 0 : volume) * 100)}%
                </span>
              </div>

              <button
                type="button"
                data-testid="music-open-submit-button"
                onClick={() => setShowSubmit(true)}
                className="mt-4 w-full rounded-full py-2 px-3 text-sm font-bebas tracking-[0.28em] text-zinc-300 hover:text-white border border-[#9945FF]/40 hover:border-[#14F195] hover:bg-[#9945FF]/10 transition-colors flex items-center justify-center gap-1.5"
              >
                <Plus size={14} /> SUBMIT A TRACK
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* The disc itself — always visible (minimized state) */}
        <button
          type="button"
          onClick={togglePlay}
          aria-label={playing ? "Pause music" : "Play music"}
          data-testid="music-disc-button"
          className="group relative block outline-none focus-visible:ring-2 focus-visible:ring-[#14F195] rounded-full"
          style={{
            width: "clamp(56px, 13vw, 72px)",
            height: "clamp(56px, 13vw, 72px)",
          }}
        >
          {/* Outer vinyl body with faint groove rings */}
          <span
            className="absolute inset-0 rounded-full"
            style={{
              background:
                "radial-gradient(circle at 50% 50%, #0a0a0d 0%, #050408 60%, #000 100%)",
              boxShadow:
                "0 0 24px rgba(153,69,255,0.5), inset 0 0 6px rgba(0,0,0,0.9)",
            }}
          />
          <span
            className="absolute inset-[3px] rounded-full pointer-events-none"
            style={{
              background:
                "repeating-radial-gradient(circle at 50% 50%, rgba(255,255,255,0.04) 0 1px, transparent 1px 3px)",
            }}
          />
          {/* Rotating label */}
          <picture>
            <source srcSet={DISC_AVIF} type="image/avif" />
            <source srcSet={DISC_WEBP} type="image/webp" />
            <img
              src={DISC_SRC}
              alt="Solana disc"
              loading="eager"
              decoding="async"
              className="disc-spin absolute rounded-full"
              data-spinning={playing ? "1" : "0"}
              style={{
                inset: "18%",
                width: "64%",
                height: "64%",
                boxShadow: "0 0 12px rgba(20,241,149,0.35)",
              }}
            />
          </picture>
          {/* Center hole */}
          <span
            className="absolute rounded-full bg-black"
            style={{ inset: "46%", boxShadow: "inset 0 0 3px rgba(255,255,255,0.15)" }}
          />
          {/* Play/pause overlay on hover */}
          <span
            className="absolute inset-0 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/35"
            aria-hidden="true"
          >
            {playing ? <Pause size={22} className="text-[#14F195]" /> : <Play size={22} className="text-[#14F195] ml-0.5" />}
          </span>
          {/* Expand chevron badge */}
          <span
            role="button"
            aria-label={expanded ? "Collapse controls" : "Expand controls"}
            data-testid="music-expand-toggle"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
            className="absolute -top-1.5 -left-1.5 w-6 h-6 rounded-full bg-[#0a0714] border border-[#9945FF]/60 text-zinc-300 hover:text-[#14F195] flex items-center justify-center transition-colors cursor-pointer"
            style={{ boxShadow: "0 0 10px rgba(153,69,255,0.5)" }}
          >
            <ChevronDown size={14} className={`transition-transform ${expanded ? "rotate-180" : "rotate-0"}`} />
          </span>
        </button>
      </div>

      <SubmitTrackDialog open={showSubmit} onClose={() => setShowSubmit(false)} />
    </>
  );
};
